import React, { useState, useRef, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { Mail, Phone, Briefcase, Pencil, Settings, Lock } from 'lucide-react';
import axios from 'axios';
import api from '../utils/api';
import { useAvatarUpload } from '../hooks/useAvatarUpload'; // adjust path as needed
import { toast, ToastContainer } from 'react-toastify';
import useRoleAccess from '../hooks/useRoleAccess';
import 'react-toastify/dist/ReactToastify.css';
import '../styles/Profile.css';

const Profile = () => {
  const { currentUser } = useContext(AuthContext);  // ✅ place at top-level of component
  const { uploading, handleUpload } = useAvatarUpload();
  const { canUploadAvatar } = useRoleAccess();

  const [avatar, setAvatar] = useState(() =>
    currentUser?.avatar || JSON.parse(localStorage.getItem("kuddleUser") || "{}")?.avatar || "/avatars/kuddlecorebear.png"
  );
  const [shimmer, setShimmer] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const fileInputRef = useRef(null);

  console.log("🔎 currentUser in Profile.jsx:", currentUser);

  const handleChangeAvatarClick = () => setShowAvatarPicker(true);

  const handleFileSelected = async (e) => {
    const file = e.target.files[0];
    if (!file) return toast.error("No file selected!");
    if (!file.type.startsWith("image/")) return toast.error("Invalid file type.");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5MB.");

    try {
      setShimmer(true);   // start shimmer animation

      // Try first: upload with useAvatarUpload hook
      await handleUpload({
        file,
        userId: currentUser._id,
        onSuccess: (uploadedImageUrl) => {
          setAvatar(uploadedImageUrl);
          syncAvatarToStoredUser(uploadedImageUrl);

        },
        onFailure: async () => {
          toast.error("Upload via hook failed, falling back to presigned URL...");

          // Second: fallback to direct upload with presigned URL
          const signedRes = await api.post('/api/generate-upload-url', {
            fileName: file.name,
            fileType: file.type,
          });

          console.log("🛑 Full response from /api/generate-upload-url:", signedRes);
          console.log("🛑 Full response object:", signedRes);          // Axios full response
          console.log("🛑 signedRes.data:", signedRes.data);           // Just the data payload

          const signedUrl = signedRes.data?.signedUrl;
          const key = signedRes.data?.key;
          const publicUrl = signedRes.data?.publicUrl;

          if (!signedUrl || !key) {
            console.error("❌ Missing signed URL or key from response");
            return toast.error("Failed to get upload URL.");
          }

          console.log("📝 Got signed URL:", signedUrl);
          console.log("🧠 Uploading to S3 key:", key);

          // ✅ Upload file directly to S3
          const uploadRes = await axios.put(signedUrl, file, {
            headers: { 'Content-Type': file.type },
          });

          console.log("✅ Upload successful!");
          console.log("Status:", uploadRes.status, uploadRes.statusText);

          const uploadedImageUrl = publicUrl || signedUrl.split('?')[0];
          console.log("🖼️ Uploaded image URL:", uploadedImageUrl);

          const userId = currentUser?._id;
          if (!userId) {
            toast.error("User ID missing. Cannot update avatar.");
            return;
          }

          const updateRes = await api.post('/api/profile/update-avatar', {
            userId,
            avatarUrl: uploadedImageUrl,
          });
          console.log("✅ Avatar updated in DB:", updateRes.data);

          ; setAvatar(uploadedImageUrl);
          syncAvatarToStoredUser(uploadedImageUrl);

        }
      });

    } catch (err) {
      console.error("❌ Error during upload or DB update:", err);
      if (err.response) {
        console.error("Server responded with:", err.response.status, err.response.statusText);
        console.error("Error body:", err.response.data);
      } else if (err.request) {
        console.error("❌ No response received:", err.request);
      } else {
        console.error("❌ Unexpected error:", err.message);
      }
      toast.error("Upload failed. Please check your connection and try again.");
    } finally {
      setShimmer(false);
    }
  };

  const syncAvatarToStoredUser = (newAvatar) => {
    const storedUser = JSON.parse(localStorage.getItem("kuddleUser") || "{}");

    const updatedUser = {
      ...storedUser,
      avatar: newAvatar,
    };

    localStorage.setItem("kuddleUser", JSON.stringify(updatedUser));
  };

  return (
    <div className="profile-page">
      <ToastContainer position="top-center" autoClose={3000} hideProgressBar />
      <header className="profile-header">
        <div className="avatar-container">
          {shimmer ? (
            <div className="shimmer-loader"></div>
          ) : (
            <img
              src={avatar}
              alt="Profile Avatar"
              className="profile-avatar-img"
              onError={(e) => {
                console.warn("🛑 Avatar image failed to load, falling back to default.");
                e.currentTarget.src = '/avatars/kuddlecorebear.png'; // your default fallback
              }}
            />
          )}
          <button className="change-avatar-btn" onClick={handleChangeAvatarClick}>
            ✨ Change Avatar
          </button>
        </div>
        <div className="profile-header-right">
          <h1>My Kuddlecore Profile</h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            View and edit your personal information and preferences.
          </p>
        </div>
      </header>

      {showAvatarPicker && (
        <div className="avatar-picker-overlay" onClick={() => setShowAvatarPicker(false)}>
          <div className="avatar-picker-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Pick Your Avatar 🐻</h3>
            <div className="avatar-options">
              {[
                '/avatars/kuddlecorebear.png',
                '/avatars/kuddlecore-bunny.png',
                '/avatars/kuddlecore-parent.png',
                '/avatars/kuddlecore-parent1.png',
                '/avatars/kuddlecore-parent2.png',
                '/avatars/kuddlecore-parent3.png',
                '/avatars/kuddlecore-parent4.png',
                '/avatars/kuddlecore-parent5.png',
                '/avatars/kuddlecore-parent6.png',
                '/avatars/kuddlecore-parent7.png',
                '/avatars/kuddlecore-parent8.png',
                '/avatars/kuddlecore-parent9.png',
                '/avatars/kuddlecore-parent10.png',
                '/avatars/kuddlecore-parent11.png',
                '/avatars/kuddlecore-parent12.png',
                '/avatars/kuddlecore-parent13.png',
                '/avatars/kuddlecore-parent14.png',
                '/avatars/kuddlecore-parent15.png',
                '/avatars/kuddlecore-teacher.png',
                '/avatars/kuddlecore-teacher1.png',
                '/avatars/kuddlecore-teacher2.png',
                '/avatars/kuddlecore-teacher3.png',
                '/avatars/kuddlecore-teacher4.png',
                '/avatars/kuddlecore-teacher5.png',
                '/avatars/kuddlecore-teacher6.png',
                '/avatars/kuddlecore-teacher7.png',
              ].map((src, idx) => (
                <img
                  key={idx}
                  src={src}
                  alt={`Avatar ${idx}`}
                  className="avatar-option-img"
                  onClick={() => {
                    setAvatar(src);
                    syncAvatarToStoredUser(src);
                    setShowAvatarPicker(false);
                  }}
                />
              ))}
            </div>

            <div className="upload-avatar-option">
              <button
                className="upload-avatar-btn"
                onClick={() => fileInputRef.current.click()}
              >
                📷 Upload Your Photo
              </button>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleFileSelected}
                style={{ display: 'none' }}
              />
            </div>

            <button className="close-picker-btn" onClick={() => setShowAvatarPicker(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <section className="card-grid">
        <div className="card profile-card">
          <div className="card-icon"><Mail size={22} /></div>
          <div className="card-content">
            <h3>Basic Information</h3>
            <p>Name, email, address, medical history</p>
            <span className="badge info">Primary</span>
          </div>
        </div>
        <div className="card profile-card">
          <div className="card-icon"><Phone size={22} /></div>
          <div className="card-content">
            <h3>Phone</h3>
            <p>(312) 555-0192</p>
            <span className="badge success">Verified</span>
          </div>
        </div>
        <div className="card profile-card">
          <div className="card-icon"><Briefcase size={22} /></div>
          <div className="card-content">
            <h3>Role</h3>
            <p>Administrator</p>
            <span className="badge warning">Full Access</span>
          </div>
        </div>
        <div className="card profile-card">
          <div className="card-icon"><Pencil size={22} /></div>
          <div className="card-content">
            <h3>Edit Profile</h3>
            <p>Update your personal details and login credentials</p>
            <button className="edit-btn">Edit</button>
          </div>
        </div>
        <div className="card profile-card">
          <div className="card-icon"><Settings size={22} /></div>
          <div className="card-content">
            <h3>Preferences</h3>
            <p>Notification settings, theme, language settings</p>
          </div>
        </div>
        <div className="card profile-card">
          <div className="card-icon"><Lock size={22} /></div>
          <div className="card-content">
            <h3>Security</h3>
            <p>Update password and view active sessions</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Profile;
